<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  button: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  formErrors: {
    type: Object,
    default: () => ({}),
  },
  canRemove: {
    type: Boolean,
    default: false,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:field', 'remove']);

const { t } = useI18n();

// Extract config with defaults
const i18nPrefix = computed(
  () => props.config.i18nPrefix || 'CONTENT_TEMPLATES'
);
const limits = computed(() => props.config.limits || {});
const fieldMapping = computed(
  () =>
    props.config.fieldMapping || {
      title: 'title',
    }
);
const showIdField = computed(() => props.config.showIdField !== false);

// Field names based on platform
const titleField = computed(() => fieldMapping.value.title || 'title');

// Computed limits
const getTitleMaxLength = computed(
  () =>
    limits.value.BUTTON_TEXT_MAX_LENGTH ??
    limits.value.QUICK_REPLY_BUTTON_TITLE_MAX_LENGTH ??
    20
);

const getIdMaxLength = computed(
  () => limits.value.QUICK_REPLY_BUTTON_ID_MAX_LENGTH ?? 200
);

// Get button values using the correct field names
const buttonTitle = computed(() => props.button[titleField.value] || '');

const handleFieldChange = (field, value) => {
  emit('update:field', field, value);
};

const handleRemove = event => {
  event?.stopPropagation();
  emit('remove');
};
</script>

<template>
  <div class="flex items-start gap-2">
    <div class="flex-1 flex flex-col gap-3">
      <!-- Title field -->
      <div class="flex flex-col gap-1">
        <Input
          :model-value="buttonTitle"
          :label="
            t(`${i18nPrefix}.CREATE.QUICK_REPLY.BUTTON_TITLE_LABEL`, {
              number: index + 1,
            })
          "
          :placeholder="
            t(`${i18nPrefix}.CREATE.QUICK_REPLY.BUTTON_TITLE_PLACEHOLDER`)
          "
          :message="formErrors[`button_${index}_${titleField}`]"
          :message-type="
            formErrors[`button_${index}_${titleField}`] ? 'error' : 'info'
          "
          :max-length="getTitleMaxLength"
          @update:model-value="value => handleFieldChange(titleField, value)"
        />
      </div>

      <!-- ID field (only for Twilio/some platforms) -->
      <div v-if="showIdField" class="flex flex-col gap-1">
        <Input
          :model-value="button.id"
          :label="t(`${i18nPrefix}.CREATE.QUICK_REPLY.BUTTON_ID_LABEL`)"
          :placeholder="
            t(`${i18nPrefix}.CREATE.QUICK_REPLY.BUTTON_ID_PLACEHOLDER`)
          "
          :message="formErrors[`button_${index}_id`]"
          :message-type="formErrors[`button_${index}_id`] ? 'error' : 'info'"
          :max-length="getIdMaxLength"
          @update:model-value="value => handleFieldChange('id', value)"
        />
      </div>
    </div>

    <!-- Remove button -->
    <button
      v-if="canRemove"
      type="button"
      class="mt-8 p-2 rounded-lg hover:bg-n-red/10 text-n-red transition-colors"
      :aria-label="t(`${i18nPrefix}.CREATE.QUICK_REPLY.REMOVE_BUTTON`)"
      @click="handleRemove"
    >
      <i class="i-lucide-trash-2 text-base" />
    </button>
  </div>
</template>
